import { api } from './client';
import { usePermissionStore } from '@core/stores/permissions.store';
import {
  PERMISSION_ACTIONS,
  PERMISSION_GROUPS,
  type PermissionsData,
} from '@core/permissions/actions';
import type { ApiResponse } from './types';

interface PermissionsPayload {
  /** ⚠️ A SIBLING of `permissions`, not a member of it. See `PermissionsData.isDelegatee`. */
  is_delegatee?: boolean;
  permissions?: {
    actions?: Record<string, boolean>;
    groups?: Record<string, { visible?: boolean; actions?: string[] }>;
  };
}

/**
 * Ports `core/services/permission.service.ts`'s HTTP half.
 *
 * `silent: true` because a permissions failure must not toast — the layout renders a
 * spinner or falls back to persisted permissions, and an error toast on every page
 * load when the backend is down is pure noise. The Angular version also subscribed
 * with an empty error handler for the same reason.
 */
export const permissionApi = {
  async loadPermissions(): Promise<PermissionsData> {
    const { data } = await api.get<ApiResponse<PermissionsPayload>>('/users/api/permissions', {
      meta: { silent: true },
    });

    const permissions: PermissionsData = {
      actions: data?.data?.permissions?.actions ?? null,
      groups: data?.data?.permissions?.groups ?? null,
      // `=== true`, not truthiness: an absent flag on an older backend must read as "not a delegatee"
      // rather than inheriting whatever falsy value arrived.
      isDelegatee: data?.data?.is_delegatee === true,
    };

    if (import.meta.env.DEV) warnOnPermissionDrift(permissions);

    usePermissionStore.getState().setPermissions(permissions);
    return permissions;
  },
};

/**
 * Dev-only drift check between the server's permission set and our const-asserted unions.
 *
 * `PERMISSION_ACTIONS` was originally derived by grepping the Angular source, which
 * under-counted by six actions and three groups — the backend issues permissions the old app
 * never checked. Without this warning, the next backend addition would be invisible until
 * someone tried to gate on it and hit a TypeScript error with no explanation.
 *
 * Only logs; never throws. An unknown permission is not an error, just news.
 */
function warnOnPermissionDrift(permissions: PermissionsData): void {
  const knownActions = new Set<string>(PERMISSION_ACTIONS);
  const knownGroups = new Set<string>(PERMISSION_GROUPS);

  const unknownActions = Object.keys(permissions.actions ?? {}).filter(
    (action) => !knownActions.has(action),
  );
  const unknownGroups = Object.keys(permissions.groups ?? {}).filter(
    (group) => !knownGroups.has(group),
  );

  if (unknownActions.length > 0) {
    console.warn(
      `[permissions] Server returned ${unknownActions.length} action(s) missing from ` +
        `PERMISSION_ACTIONS in core/permissions/actions.ts: ${unknownActions.join(', ')}`,
    );
  }
  if (unknownGroups.length > 0) {
    console.warn(
      `[permissions] Server returned ${unknownGroups.length} group(s) missing from ` +
        `PERMISSION_GROUPS in core/permissions/actions.ts: ${unknownGroups.join(', ')}`,
    );
  }
}
